import { FunctionComponent } from 'react';
import clsx from 'clsx';
import styles from './BoardingDataInfo.module.scss';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { DataVaraible, AppointmentMultiple, PetServiceWrapper, itemTypes, ItemData } from 'model/NewMultipleBooking';
import { Product } from '@spike/product-model';
import AddOnService from '@spike/addon-service-model';
import Service from 'model/Service';
import moment, { Moment } from 'moment-timezone';
import ServiceOptions from './ServiceOptions';
import FeedingsAndMedicationsInfo from './FeedingsAndMedicationsInfo';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMoon } from '@fortawesome/pro-solid-svg-icons';
import ServiceItemsInfo from './ServiceItemsInfo';
import { resolveAppointmentCardLineBase, resolveAppointmentCardLineDisplay } from '../utils/membershipDisplayPrice';

interface Props {
    hasFeedings: boolean;
    hasMedications: boolean;
    data: DataVaraible;
    index: number;
    appts: Array<DataVaraible>;
    appointment: AppointmentMultiple;
    onEdit: (
        petId: number,
        dateEdit: Moment,
        staffId?: number,
        itemData?: ItemData,
        service?: PetServiceWrapper,
        product?: Product,
        addOn?: AddOnService,
        custom?: string,
        type?: itemTypes,
        dateEnd?: Moment
    ) => void;
    onDeleteAppointment: (petId: number, value?: number | string, type?: itemTypes, dateToRemove?: Moment) => void;
    onShowActions: (
        petId: number,
        dateEdit: Moment,
        itemData?: ItemData,
        service?: PetServiceWrapper,
        staffId?: number,
        product?: Product,
        addOn?: AddOnService,
        custom?: string,
        type?: itemTypes,
        dateEnd?: Moment
    ) => void;
}

export const BoardingDataInfo: FunctionComponent<Props> = props => {
    const services = useSelector<RootState, Array<Service>>(state => state.services.services);

    const slotLinePrice = Number(props.data.service?.price) || 0;
    const lineBase = resolveAppointmentCardLineBase({
        slotPrice: slotLinePrice,
        itemDataPrice: props.data.itemData?.price,
        beforePrice: props.data.service?.beforePrice,
        customPriceAmount: props.data.service?.customPriceAmount
    });
    const {
        displayPrice: resolvedDisplayPrice,
        originalPrice: displayPriceBeforeDiscount,
        hasDisplayDiscount
    } = resolveAppointmentCardLineDisplay({
        enrollmentId: props.data.service?.enrollment?.id,
        membershipItem: props.data.service?.membershipItem,
        slotPrice: slotLinePrice,
        basePrice: lineBase,
        itemDiscount: props.data.itemData?.discount ?? props.data.service?.discount
    });

    return (
        <>
            {props.index === 0 && (
                <div className={styles.dateContainer}>
                    <span className={styles.date}>{props.data.date?.format('dddd, DD MMM YYYY')}</span>
                </div>
            )}
            <div
                className={styles.serviceStaffContainer}
                key={props.data.service?.service?.id}
            >
                <div className={styles.itemContainer}>
                    <div
                        className={styles.emptyBox}
                        style={{
                            borderLeftColor:
                                services.find((serv: Service) => serv.id === props.data.service?.service?.id)?.color ??
                                '#ffffff'
                        }}
                    />
                    <div
                        className={clsx(styles.divide, {
                            [styles.withoutDivider]: props.index === props.appts.length - 1
                        })}
                        style={{ width: '91%' }}
                    >
                        <span className={clsx(styles.service, styles.marginTop)}>
                            <span className={clsx(styles.serviceNameText, styles.rightSpace)}>
                                {props.data.service?.service?.name}
                            </span>
                            ·
                            {hasDisplayDiscount ? (
                                <>
                                    <span className={clsx(styles.beforePrice, styles.price)}>
                                        ${displayPriceBeforeDiscount.toFixed(2)}
                                    </span>
                                    <span className={styles.price}>${resolvedDisplayPrice.toFixed(2)}/Night</span>
                                </>
                            ) : (
                                <span className={styles.price}>{'$' + resolvedDisplayPrice.toFixed(2)}/Night</span>
                            )}
                        </span>
                        <div className={styles.boardingData}>
                            <span className={styles.serviceName}>
                                <span className={clsx(styles.serviceNameText, styles.rightSpace, styles.widthItem)}>
                                    Check In
                                </span>
                                <span className={clsx(styles.priceAndDuration, styles.spacing)}>
                                    {props.data.date?.format('ll')} · {props.data.date?.format('LT')}
                                </span>
                            </span>
                            <span className={clsx(styles.serviceName, styles.centered)}>
                                <span className={clsx(styles.serviceNameText, styles.rightSpace, styles.widthItem)}>
                                    Check Out
                                </span>
                                <span className={clsx(styles.priceAndDuration, styles.spacing)}>
                                    {props.data.dateEnd?.format('ll')} · {props.data.dateEnd?.format('LT')}
                                </span>
                            </span>
                            <span className={clsx(styles.serviceName, styles.centered, styles.lastitem)}>
                                <span className={clsx(styles.serviceNameText, styles.rightSpace, styles.widthItem)}>
                                    Nights
                                </span>
                                <span className={clsx(styles.priceAndDuration, styles.spacing)}>
                                    <FontAwesomeIcon
                                        icon={faMoon}
                                        className={styles.iconNights}
                                    />
                                    {Math.max(
                                        0,
                                        moment(props.data?.dateEnd)
                                            .startOf('day')
                                            .diff(moment(props.data?.date).startOf('day'), 'days')
                                    )}{' '}
                                    Nights
                                </span>
                            </span>
                        </div>
                    </div>
                    <div
                        className={clsx(styles.divide, styles.serviceOptionsContainer, {
                            [styles.withoutDivider]: props.index === props.appts.length - 1
                        })}
                    >
                        <ServiceOptions
                            className={styles.serviceOptions}
                            item="Service"
                            onClickMobile={() =>
                                props.onShowActions(
                                    props.appointment.petId,
                                    props.data.date,
                                    props.data.itemData,
                                    props.data.service,
                                    props.data.staffId,
                                    props.data.product,
                                    props.data.addOn,
                                    props.data.customDescription,
                                    itemTypes.service,
                                    props.data.dateEnd
                                )
                            }
                            onEdit={() =>
                                props.onEdit(
                                    props.appointment.petId,
                                    props.data.date,
                                    props.data.staffId,
                                    props.data.itemData,
                                    props.data.service,
                                    props.data.product,
                                    props.data.addOn,
                                    props.data.customDescription,
                                    itemTypes.service,
                                    props.data.dateEnd
                                )
                            }
                            onDeleteAppointment={() =>
                                props.onDeleteAppointment(
                                    props.appointment.petId,
                                    props.data.service?.service?.id,
                                    itemTypes.service,
                                    props.data.date
                                )
                            }
                        />
                    </div>
                </div>
                <FeedingsAndMedicationsInfo
                    hasFeedings={props.hasFeedings}
                    hasMedications={props.hasMedications}
                />
            </div>
        </>
    );
};

export default BoardingDataInfo;
